import React from 'react';
import { Play, ExternalLink } from 'lucide-react';
import { VideoModal } from './video-modal';

interface VideoItem {
  url: string;
  title: string;
  thumbnail?: string;
  author?: string;
  length?: string;
  iframe_src?: string;
  content?: string;
}

interface VideoCarouselProps {
  videos: VideoItem[];
}

export function VideoCarousel({ videos }: VideoCarouselProps) {
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const [selectedVideo, setSelectedVideo] = React.useState<VideoItem | null>(null);
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  const handleScroll = (direction: 'left' | 'right') => {
    if (!scrollContainerRef.current) return;
    const container = scrollContainerRef.current;
    const scrollAmount = container.clientWidth * 0.8; // Scroll 80% of visible width
    const currentScroll = container.scrollLeft;
    const maxScroll = container.scrollWidth - container.clientWidth;
    
    if (direction === 'left') {
      container.scrollTo({
        left: Math.max(0, currentScroll - scrollAmount),
        behavior: 'smooth',
      });
    } else {
      container.scrollTo({
        left: Math.min(maxScroll, currentScroll + scrollAmount),
        behavior: 'smooth',
      });
    }
  };
  
  // Check if we can scroll left/right
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(true);
  
  React.useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    
    const updateScrollButtons = () => {
      const { scrollLeft, scrollWidth, clientWidth } = container;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    };
    
    // Initial check with delay to ensure DOM is ready
    setTimeout(updateScrollButtons, 100);
    container.addEventListener('scroll', updateScrollButtons);
    window.addEventListener('resize', updateScrollButtons);
    
    // Also observe DOM changes
    const observer = new MutationObserver(() => {
      setTimeout(updateScrollButtons, 50);
    });
    observer.observe(container, { childList: true, subtree: true, attributes: true });
    
    return () => {
      container.removeEventListener('scroll', updateScrollButtons);
      window.removeEventListener('resize', updateScrollButtons);
      observer.disconnect();
    };
  }, [videos.length]);

  if (videos.length === 0) {
    return null;
  }

  // Log for debugging
  console.log(`[VideoCarousel] Rendering ${videos.length} videos`);

  return (
    <div className="relative w-full" style={{ minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>
      {/* Scroll buttons - positioned inside container */}
      {canScrollLeft && (
        <button
          type="button"
          onClick={() => handleScroll('left')}
          className="absolute left-2 top-1/2 -translate-y-1/2 z-20 bg-white/95 hover:bg-white border border-gray-300 rounded-full p-2 shadow-lg transition-all"
          aria-label="Scroll left"
        >
        <svg
          className="w-4 h-4 text-gray-700"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 19l-7-7 7-7"
          />
        </svg>
        </button>
      )}
      {canScrollRight && (
        <button
          type="button"
          onClick={() => handleScroll('right')}
          className="absolute right-2 top-1/2 -translate-y-1/2 z-20 bg-white/95 hover:bg-white border border-gray-300 rounded-full p-2 shadow-lg transition-all"
          aria-label="Scroll right"
        >
        <svg
          className="w-4 h-4 text-gray-700"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 5l7 7-7 7"
          />
        </svg>
        </button>
      )}

      {/* Video cards - scrollable container */}
      <div
        ref={scrollContainerRef}
        className="flex gap-3 overflow-x-auto overflow-y-hidden pb-3 px-1"
        style={{
          scrollbarWidth: 'thin',
          scrollbarColor: '#cbd5e0 #f7fafc',
          WebkitOverflowScrolling: 'touch',
          scrollBehavior: 'smooth',
          maxWidth: '100%',
        }}
      >
        {videos.map((video, index) => (
          <div
            key={index}
            className="flex-shrink-0 bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow"
            style={{ width: '280px', minWidth: '280px', maxWidth: '280px' }}
          >
            {/* Thumbnail - Clickable to open modal */}
            <button
              type="button"
              onClick={() => {
                setSelectedVideo(video);
                setIsModalOpen(true);
              }}
              className="relative w-full aspect-video bg-gray-100 cursor-pointer group"
            >
              {video.thumbnail ? (
                <img
                  src={video.thumbnail}
                  alt={video.title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    // Fallback to placeholder if image fails to load
                    (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="320" height="180"%3E%3Crect fill="%23e5e7eb" width="320" height="180"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%239ca3af" font-family="sans-serif" font-size="14"%3ENo thumbnail%3C/text%3E%3C/svg%3E';
                  }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-100">
                  <Play className="w-12 h-12 text-gray-400" />
                </div>
              )}
              {/* Play overlay */}
              <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors">
                <div className="bg-white/90 rounded-full p-3 group-hover:scale-110 transition-transform">
                  <Play className="w-6 h-6 text-gray-900 fill-gray-900" />
                </div>
              </div>
            </button>

            {/* Content */}
            <div className="p-4">
              <h3 className="font-semibold text-sm text-gray-900 line-clamp-2 mb-2">
                {video.title}
              </h3>
              
              <div className="space-y-1 text-xs text-gray-600">
                {video.author && (
                  <div className="flex items-center gap-1">
                    <span className="font-medium">Tác giả:</span>
                    <span>{video.author}</span>
                  </div>
                )}
                {video.length && (
                  <div className="flex items-center gap-1">
                    <span className="font-medium">Độ dài:</span>
                    <span>{video.length}</span>
                  </div>
                )}
              </div>

              {video.content && (
                <p className="text-xs text-gray-500 line-clamp-2 mt-2">
                  {video.content}
                </p>
              )}

              {/* Actions */}
              <div className="mt-3 flex gap-2">
                <a
                  href={video.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 bg-primary text-white text-xs font-medium rounded hover:bg-primary/90 transition-colors"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  Xem video
                </a>
                {video.iframe_src && (
                  <a
                    href={video.iframe_src}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-1.5 border border-gray-300 text-gray-700 text-xs font-medium rounded hover:bg-gray-50 transition-colors"
                  >
                    Embed
                  </a>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Video Modal */}
      <VideoModal
        isOpen={isModalOpen}
        video={selectedVideo}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedVideo(null);
        }}
      />
    </div>
  );
}

